// Copyright 2026 Picallex Holding Group. All rights reserved.
//
// Guard de las rutas con `requiresAuth`.
//
// El token de Auth0 dura 24 h y vive en sessionStorage. El guard lo vuelve a
// verificar en cada navegacion, y antes cualquier falla terminaba en el 401:
// un token vencido (pestaña abierta mas de un dia) o no poder bajar las llaves
// de Auth0 (la red todavia no volvio al despertar la PC). Ninguno de los dos
// es "no autorizado", y la pantalla del 401 solo ofrecia desloguearse.
// Ahora, si la verificacion falla, se pide un token nuevo a Auth0; el 401
// queda solo para un token valido sin el scope del panel.
import { Route, NavigationGuardNext } from 'vue-router';
import { jwtVerify, createRemoteJWKSet } from 'jose';
import * as Sentry from '@sentry/vue';
import { getInstance } from '@/auth';

interface Auth0 {
    loading: boolean;
    getTokenSilently(): Promise<string | undefined>;
    $watch(expression: string, callback: (value: boolean) => void): () => void;
}

const AUTH0_READY_TIMEOUT_MS = 10_000;

let jwks: ReturnType<typeof createRemoteJWKSet> | null = null;

// Una sola instancia: jose guarda las llaves en memoria. Antes se creaba en
// cada navegacion y cada cambio de vista volvia a bajarlas de Auth0.
function keys() {
    if (!jwks) {
        jwks = createRemoteJWKSet(new URL(process.env.VUE_APP_AUTH0_JWKS as string));
    }

    return jwks;
}

function sortedScopes(scope: unknown): string {
    return String(scope || '').split(' ').sort().join(' ');
}

// true si el token es valido y trae exactamente los scopes del panel.
// Lanza si no se puede verificar: vencido, firma invalida o sin llaves.
async function hasPanelScope(token: string): Promise<boolean> {
    const { payload } = await jwtVerify(token, keys(), {
        issuer: process.env.VUE_APP_AUTH0_ISSUER,
        audience: process.env.VUE_APP_AUTH0_AUDIENCE,
    });

    return sortedScopes(payload.scope) === sortedScopes(process.env.VUE_APP_AUTH0_SCOPE);
}

// En la primera navegacion el guard corre antes de que el SDK de Auth0
// termine de inicializarse (created() de auth.ts es async).
function auth0Ready(): Promise<Auth0> {
    const auth = getInstance() as unknown as Auth0;

    if (!auth.loading) {
        return Promise.resolve(auth);
    }

    return new Promise((resolve, reject) => {
        const timer = setTimeout(
            () => reject(new Error('Auth0 did not finish loading')),
            AUTH0_READY_TIMEOUT_MS,
        );

        const stop = auth.$watch('loading', (loading) => {
            if (!loading) {
                clearTimeout(timer);
                stop();
                resolve(auth);
            }
        });
    });
}

// Pide un token a Auth0 y lo deja en sessionStorage, que es de donde lo leen
// el guard, el socket y las llamadas al api. Mientras la sesion de Auth0 siga
// viva no le pide la contraseña al usuario.
export async function renewToken(): Promise<string> {
    const auth = await auth0Ready();
    const token = await auth.getTokenSilently();

    if (!token) {
        throw new Error('Auth0 did not return a token');
    }

    sessionStorage.setItem('token', token);
    return token;
}

export default async function authGuard(
    to: Route,
    _from: Route,
    next: NavigationGuardNext,
): Promise<void> {
    if (!to.matched.some((record) => record.meta.requiresAuth)) {
        next();
        return;
    }

    const token = sessionStorage.getItem('token');

    if (!token) {
        next({ path: '/', query: { redirect: to.fullPath } });
        return;
    }

    const unauthorized = { path: '/unauthorized', query: { redirect: to.fullPath } };

    try {
        if (await hasPanelScope(token)) {
            next();
        } else {
            next(unauthorized);
        }

        return;
    } catch (e) {
        // el nombre del error dice la causa: JWTExpired (pestaña de mas de
        // 24 h) o JWKSTimeout / TypeError de fetch (sin red al despertar)
        console.error(e);
        Sentry.captureException(e, { tags: { authGuard: 'verify' } });
    }

    try {
        if (await hasPanelScope(await renewToken())) {
            next();
        } else {
            next(unauthorized);
        }
    } catch (e) {
        console.error(e);
        Sentry.captureException(e, { tags: { authGuard: 'renew' } });
        // no se pudo verificar ni renovar: la pantalla ofrece reintentar en
        // vez de obligar a loguearse de nuevo
        next({ ...unauthorized, query: { ...unauthorized.query, retry: '1' } });
    }
}
